import { NavBarMenuItemProps, NavBarMenuSectionProps } from "components/nav-bars/types";
import _ from "lodash";
import { matchPath, RouteObject } from "react-router";
import { APP_URLS } from "urls/AppUrls";
import { PERMANENT_URLS } from "urls/constants";
import { sentenceCase } from "change-case";

export const isStringEquals = (source: string, compareTo: string) => source.toLowerCase().trim() === compareTo.toLowerCase().trim();

export const isPropertyNullOrUndefined = (object: any, path: string) =>
  !_.has(object, path) || _.isUndefined(object[path]) || _.isNull(object[path]);

export const mergArrayByKey = <TObject, TSource>(
  object: TObject[],
  source: TSource[],
  key: string
): TObject[] & TSource[] => _.toArray(_.merge(_.keyBy(object, key), _.keyBy(source, key)));

type FlattenAppPath = { [key: string]: any };

const objectToPathsValues = (item: any): FlattenAppPath => {
  let newObj = {};
  let path = "";

  flattingNestedObject(item, path, newObj);
  return newObj;
};

const flattingNestedObject = (item: any, path: string, output: FlattenAppPath): void => {
  Object.keys(item).forEach((key) => {
    const combinedPath = path ? `${path}.${key}` : key;
    if (typeof item[key] === "object") {
      flattingNestedObject(item[key], combinedPath, output);
    } else {
      output[combinedPath] = item[key];
    }
  });
};

// Add permanent urls to feature enabled to get list of features that should be enabled.
// This is formatted using a flatten form of the keys in APP_URLS with a "." to denote a hierarcy
//
// For example,
//  REPORTS: {
//    ROOT: path(AUTH_ROOT, "/reports"),
//    .....
// would be REPORTS.ROOTS
const getEnabledPaths = (featuresEnabled: string[]) => {
  const features = _.uniq<string>([...PERMANENT_URLS, ...featuresEnabled]);

  // Form flatten dictionary of APP_URLS with path
  const allPaths = objectToPathsValues(APP_URLS);
  const enabledPaths: FlattenAppPath = {};
  Object.keys(allPaths).forEach((key) => {
    if (features.includes(key)) {
      enabledPaths[key] = allPaths[key];
    }
  });

  return enabledPaths;
};

const doesPathMatch = (enabledPaths: FlattenAppPath, path?: string): boolean =>
  _.find(enabledPaths, (value: string) => {
    if (value === APP_URLS.ALL || value === APP_URLS.ROOT) {
      return false;
    }

    if (path) {
      const foundMatchingRoute = matchPath(
        {
          path: value,
        },
        path!
      );

      return value === path! || !_.isNull(foundMatchingRoute);
    }
    return;
  })
    ? true
    : false;

const addEnabledItem = (
  enabledPaths: FlattenAppPath,
  item: NavBarMenuItemProps,
  enabledItems: Array<NavBarMenuItemProps>
) => {
  if (!!item.children) {
    const children: Array<NavBarMenuItemProps> = [];
    item.children.forEach((c) => addEnabledItem(enabledPaths, c, children));

    // Special conditions for favourites
    if (children.length > 0) {
      // Incase model is immutable
      let model = { ...item };
      model.children = children;
      enabledItems.push(model);
    }
    return;
  }

  const matched = item.alwaysShow === true || doesPathMatch(enabledPaths, item.path);

  if (matched) {
    enabledItems.push(item);
    return;
  }

  return;
};
export const getMenuRoutesByIFeature = (
  menuSections: NavBarMenuSectionProps[],
  featuresEnabled: string[]
): NavBarMenuSectionProps[] => {
  const enabledPaths = getEnabledPaths(featuresEnabled);
  let enabledMenuSections: NavBarMenuSectionProps[] = [];
  menuSections.forEach((section: NavBarMenuSectionProps) => {
    let enabledItems: Array<NavBarMenuItemProps> = [];
    section.items.forEach((item: NavBarMenuItemProps) => {
      let enabledSectionItems: Array<NavBarMenuItemProps> = [];
      addEnabledItem(enabledPaths, item, enabledSectionItems);

      if (enabledSectionItems.length > 0) {
        enabledItems = [...enabledItems, ...enabledSectionItems];
      }
    });

    if (enabledItems.length > 0) {
      enabledMenuSections.push({
        subheader: section.subheader,
        permissions: section.permissions,
        items: enabledItems,
      });
    }
  });
  return enabledMenuSections;
};

const addEnabledRoute = (
  enabledPaths: FlattenAppPath,
  route: RouteObject,
  enabledRoutes: Array<RouteObject>
) => {
  if (!!route.children) {
    const children: Array<RouteObject> = [];
    route.children.forEach((c) => addEnabledRoute(enabledPaths, c, children));

    if (children.length > 0) {
      // Incase model is immutable
      let model = { ...route };
      model.children = children;
      enabledRoutes.push(model);
    }
    return;
  }

  const matched = doesPathMatch(enabledPaths, route.path);

  if (matched) {
    enabledRoutes.push(route);
    return;
  }

  return;
};

export const getRoutesByIFeature = (
  routes: RouteObject[],
  featuresEnabled: string[]
): RouteObject[] => {
  const enabledPaths = getEnabledPaths(featuresEnabled);

  let enabledRoutes: RouteObject[] = [];
  routes.forEach((route: RouteObject) => {
    // Special condition for root route as this is always required
    if (route.path === APP_URLS.ROOT) {
      enabledRoutes.push(route);
    } else {
      let enabledRoute: Array<RouteObject> = [];
      addEnabledRoute(enabledPaths, route, enabledRoute);

      if (enabledRoute.length > 0) {
        enabledRoutes = [...enabledRoutes, ...enabledRoute];
      }
    }
  });

  return enabledRoutes;
};

// Function to determine if a node is a leaf node
export const isLeaf = (node: any, data: any) =>
  !data.some((item: any) => item.parentId === node.id);


export function preservePunctuation(str: string) {
  // Save punctuation and its position
  let punctuation = [];
  for (let i = 0; i < str.length; i++) {
    if ([".", ",", "!", "?", ":", ";","'"].includes(str[i])) {
      punctuation.push({ char: str[i], pos: i });
    }
  }

  // Convert to sentence case
  let converted = sentenceCase(str);

  // Re-insert punctuation at the original positions
  punctuation.forEach((item) => {
    converted = converted.slice(0, item.pos) + item.char + converted.slice(item.pos);
  });

  return converted;
}

export function isValidIdentifierUnderScore(input:string) {
  const regex = /^[a-zA-Z][a-zA-Z0-9_]*$/;
  return regex.test(input);
}

export function isValidIdentifierSpace(input: string) {
  // Regular expression: first character must be a letter, rest can be letters, digits, or spaces
  const regex = /^[a-zA-Z][a-zA-Z0-9\s]*$/;
  return regex.test(input);
}

export function convertToPascalCase(input:string) {
  if(!input) return ''
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .split(' ') // Split the string into words by space
    .map(word => {
      // Check if the word contains PMS and preserve PMS capitalization
      if (word.includes('PMS')) {
        const parts = word.split('PMS');
        const pascalParts = parts.map((part, index) => {
          if (index === 0 && part !== '') {
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(); // Capitalize the first part
          }
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(); // Capitalize after PMS
        });
        return pascalParts.join('PMS'); // Join back with PMS
      }
      // Otherwise, convert the word to PascalCase
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(''); // Join the words back into a PascalCase string
}

export function createTag(text?: string): string {
  if (!text) {
    return '';
  }

  const rawTextAndSpaces = text.replace(/[^a-zA-Z\s]/gi, '');
  const words = rawTextAndSpaces.split(' ');

  let tag = '';

  if (words.length === 2) {
    tag = (words[0].slice(0, 2) + words[1][0]).toUpperCase();
  } else if (words.length > 2) {
    tag = words.map(word => word[0]).join('').toUpperCase();
  } else {
    tag = rawTextAndSpaces.slice(0, 3).toUpperCase();
  }

  // Ensure the tag is at least 3 characters long
  if (tag.length < 3) {
    tag = (tag + rawTextAndSpaces.slice(tag.length)).slice(0, 3).toUpperCase();
  }

  return tag;
}
